// Workstream: accounts. /account: sign in or sign up, then my packs, game history and settings.
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { PackSummary } from '@shared/types';
import { Footer, TopBar, useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { getMyPacks, getName, saveName } from '../../lib/storage';
import { accountApi, type AccountUser, type ClaimResult, type History } from './api';
import { afterSignIn, initial, setAccountUser, signOut, useAccount } from './session';

const PASSWORD_MIN = 8;

/** Only same-site paths are allowed as a post-sign-in destination. */
function safeNext(next: string | null): string | null {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

function claimMessage(result: ClaimResult | null): string {
  const n = result?.claimed.length ?? 0;
  if (!n) return '';
  return n === 1 ? ' Your pack from this device is now on your account.' : ` ${n} packs from this device are now on your account.`;
}

export function AccountPage() {
  const { status, user } = useAccount();
  useDocumentTitle(user ? 'Your account' : 'Sign in');
  return (
    <>
      <TopBar />
      <main className="page stack account-page">
        {status === 'unknown' ? <p className="muted">Loading...</p> : user ? <SignedIn user={user} /> : <SignInForm />}
      </main>
      <Footer />
    </>
  );
}

// ------------------------------------------------------------------ signed out

function SignInForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = usePrefs();
  const [mode, setMode] = useState<'login' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState(getName());
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const devicePacks = getMyPacks().length;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (mode === 'signup' && password.length < PASSWORD_MIN) return setError(`Passwords need at least ${PASSWORD_MIN} characters.`);
    setBusy(true);
    try {
      const { user } =
        mode === 'signup' ? await accountApi.signup(email, password, name.trim()) : await accountApi.login(email, password);
      if (mode === 'signup' && name.trim()) saveName(name.trim());
      const claimed = await afterSignIn(user);
      toast(`${mode === 'signup' ? 'Welcome' : 'Welcome back'}, ${user.displayName}!${claimMessage(claimed)}`, 'success');
      const next = safeNext(params.get('next'));
      if (next) navigate(next);
      else window.scrollTo(0, 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div>
        <h1>{mode === 'signup' ? 'Make an account' : 'Sign in'}</h1>
        <p className="muted">
          Totally optional. Everyone can play as a guest. An account keeps your packs on every phone and remembers the games you
          have played.
        </p>
      </div>
      <div className="segmented" role="group" aria-label="Sign in or sign up">
        <button type="button" aria-pressed={mode === 'login'} onClick={() => setMode('login')}>
          Sign in
        </button>
        <button type="button" aria-pressed={mode === 'signup'} onClick={() => setMode('signup')}>
          New here? Sign up
        </button>
      </div>
      <form className="panel stack" onSubmit={submit}>
        {mode === 'signup' && (
          <label className="field">
            Your name <span className="hint">What the family calls you</span>
            <input className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="nickname" placeholder="Pinky Masi" />
          </label>
        )}
        <label className="field">
          Email
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={254}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="field">
          Password {mode === 'signup' && <span className="hint">At least {PASSWORD_MIN} characters</span>}
          <span className="password-field">
            <input
              className="input"
              type={showPassword ? 'text' : 'password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={mode === 'signup' ? PASSWORD_MIN : undefined}
              maxLength={200}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPassword((s) => !s)} aria-pressed={showPassword}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </span>
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? 'One sec...' : mode === 'signup' ? 'Create my account' : 'Sign in'}
        </button>
        <p className="muted small" style={{ margin: 0 }}>
          {mode === 'signup'
            ? "We never send emails, so there is no reset link. Pick a password you'll remember. You can change it any time while signed in."
            : "Forgot your password? We don't send emails, so we can't reset it. If you're still signed in on another phone, change it there."}
        </p>
      </form>
      {devicePacks > 0 && (
        <p className="notice">
          {devicePacks === 1 ? 'The pack you made on this device' : `The ${devicePacks} packs you made on this device`} will move to your
          account when you sign in.
        </p>
      )}
    </>
  );
}

// ------------------------------------------------------------------ signed in

function SignedIn({ user }: { user: AccountUser }) {
  const { toast } = usePrefs();
  const navigate = useNavigate();
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    accountApi.packs().then(setPacks).catch((e) => setError(e.message));
    accountApi.history().then(setHistory).catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  const accountCodes = new Set(packs?.map((p) => p.code) ?? []);
  const deviceOnly = packs ? getMyPacks().filter((p) => !accountCodes.has(p.code)) : [];

  async function claimDevicePacks() {
    try {
      const result = await accountApi.claim(deviceOnly.map((p) => ({ code: p.code, editKey: p.editKey })));
      const n = result.claimed.length;
      toast(
        n ? `Added ${n === 1 ? 'your pack' : `${n} packs`} to your account.` : 'Those packs belong to another account, so they stay on this device.',
        n ? 'success' : 'info',
      );
      load();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  }

  return (
    <>
      <div className="account-head">
        <span className="account-initial" aria-hidden="true">
          {initial(user.displayName)}
        </span>
        <div className="grow">
          <h1 style={{ margin: 0 }}>Namaste, {user.displayName}</h1>
          <span className="muted small">{user.email}</span>
        </div>
      </div>
      {error && <p className="error">{error}</p>}

      {history && (
        <div className="stat-grid account-stats">
          <div className="stat">
            <div className="n">{history.totals.games}</div>
            <div className="l">Games played</div>
          </div>
          <div className="stat">
            <div className="n">{history.totals.wins}</div>
            <div className="l">Games won</div>
          </div>
          <div className="stat">
            <div className="n">{history.totals.points}</div>
            <div className="l">Points scored</div>
          </div>
        </div>
      )}

      <section className="panel stack">
        <div className="row between">
          <h2 style={{ margin: 0 }}>My packs</h2>
          <Link className="btn btn-sm btn-primary" to="/packs/new">
            Make a pack
          </Link>
        </div>
        {!packs ? (
          <p className="muted">Loading...</p>
        ) : packs.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            No packs yet. Make one with cousins' names, family sayings and a few photos, then add it to any game with its code.
          </p>
        ) : (
          <ul className="player-list">
            {packs.map((p) => (
              <li key={p.code}>
                <span className="name">
                  <Link to={`/packs/${p.code}`}>{p.name}</Link>
                  <span className="muted small account-pack-meta">
                    <span className="chip orange">{p.code}</span> {plural(p.promptCount, 'prompt')} · {plural(p.answerCount, 'answer')}
                  </span>
                </span>
                <Link className="btn btn-sm btn-outline" to={`/packs/${p.code}/edit`}>
                  Edit
                </Link>
              </li>
            ))}
          </ul>
        )}
        {deviceOnly.length > 0 && (
          <div className="notice stack" style={{ gap: 8 }}>
            <span>
              {deviceOnly.length === 1 ? 'One pack on this device is' : `${deviceOnly.length} packs on this device are`} not on your
              account yet: {deviceOnly.map((p) => p.name).join(', ')}.
            </span>
            <button type="button" className="btn btn-sm btn-blue" onClick={claimDevicePacks}>
              Add to my account
            </button>
          </div>
        )}
      </section>

      <section className="panel stack">
        <h2 style={{ margin: 0 }}>Game history</h2>
        {!history ? (
          <p className="muted">Loading...</p>
        ) : history.games.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            No games yet. Finish a game while you're signed in and it shows up here.
          </p>
        ) : (
          <ul className="history-list">
            {history.games.map((g) => (
              <li key={g.gameId} className={g.won ? 'won' : ''}>
                <div className="history-when">
                  <strong>{formatDate(g.playedAt)}</strong>
                  <span className="muted small">Room {g.roomCode}</span>
                </div>
                <div className="grow history-what">
                  <strong>{g.won ? `🏆 You won with ${plural(g.score, 'point')}` : `You scored ${plural(g.score, 'point')}`}</strong>
                  <span className="muted small">
                    {plural(g.playerCount, 'player')} · {plural(g.rounds, 'round')}
                    {!g.won && g.winnerName ? ` · ${g.winnerName} won` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ProfileForm user={user} />
      <PasswordForm />

      <button
        type="button"
        className="btn btn-outline btn-block"
        onClick={async () => {
          try {
            await signOut();
            toast('Signed out. See you at the next party!');
            navigate('/');
          } catch (err) {
            toast((err as Error).message, 'error');
          }
        }}
      >
        Sign out
      </button>
    </>
  );
}

function ProfileForm({ user }: { user: AccountUser }) {
  const { toast } = usePrefs();
  const [name, setName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const changed = name.trim() !== user.displayName && name.trim().length > 0;
  return (
    <form
      className="panel stack"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const res = await accountApi.rename(name.trim());
          setAccountUser(res.user);
          saveName(res.user.displayName);
          toast('Name saved.', 'success');
        } catch (err) {
          toast((err as Error).message, 'error');
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2 style={{ margin: 0 }}>Your name</h2>
      <label className="field">
        <span className="sr-only">Display name</span>
        <input className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="nickname" />
      </label>
      <button className="btn btn-outline" disabled={!changed || busy}>
        Save name
      </button>
    </form>
  );
}

function PasswordForm() {
  const { toast } = usePrefs();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="panel stack"
      onSubmit={async (e) => {
        e.preventDefault();
        setError('');
        if (next.length < PASSWORD_MIN) return setError(`Passwords need at least ${PASSWORD_MIN} characters.`);
        setBusy(true);
        try {
          await accountApi.changePassword(current, next);
          setCurrent('');
          setNext('');
          toast('Password changed. Other phones have been signed out.', 'success');
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2 style={{ margin: 0 }}>Change password</h2>
      <p className="muted small" style={{ margin: 0 }}>
        We don't send emails, so this is the only way to change it. Other phones get signed out.
      </p>
      <label className="field">
        Current password
        <input className="input" type="password" autoComplete="current-password" required maxLength={200} value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label className="field">
        New password <span className="hint">At least {PASSWORD_MIN} characters</span>
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN}
          maxLength={200}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn-outline" disabled={busy || !current || !next}>
        Change password
      </button>
    </form>
  );
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}
